import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="lazy-invoices"
export default class extends Controller {
  static targets = ["content", "contentWrapper", "loading", "error"]
  static values = { url: String, showDue: Boolean }

  connect() {
    this.loadInvoices()
  }

  async loadInvoices() {
    try {
      this.showLoading()

      const response = await fetch(this.urlValue, {
        headers: {
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        }
      })

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`)
      }

      const data = await response.json()
      this.renderInvoices(data.invoices)
      this.showContent()

    } catch (error) {
      console.error('Failed to load invoices:', error)
      this.showError()
    }
  }

  renderInvoicesCard(invoices) {
    if (invoices.length === 0) {
      this.contentTarget.innerHTML = `
        <p class="text-gray-500 text-center py-4">No invoices yet.</p>
      `
      return
    }

    const invoiceHtml = invoices.map(invoice => {
      const date = new Date(invoice.period_start * 1000)
      const formattedDate = date.toLocaleDateString('en-US', {
        month: 'long',
        year: 'numeric'
      })

      const amount = (invoice.total / 100).toFixed(2)
      const statusHtml = invoice.overdue
        ? '<span class="text-red-600 font-medium">Overdue</span>'
        : invoice.status.charAt(0).toUpperCase() + invoice.status.slice(1)
      const dueHtml = invoice.due_date && invoice.status === 'open'
        ? ` · Due ${this.formatDueDate(invoice.due_date)}`
        : ''

      return `
        <div class="flex items-center justify-between py-2">
          <div>
            <div class="text-sm font-medium text-gray-900">${formattedDate}</div>
            <div class="text-sm text-gray-500">${statusHtml}${dueHtml}</div>
          </div>
          <div class="text-right">
            <div class="text-sm font-medium text-gray-900">$${amount}</div>
            <a href="${invoice.hosted_invoice_url}" target="_blank" class="text-sm text-blue-600 hover:text-blue-700 inline-flex items-center">
              View
              <svg class="w-3 h-3 ml-1" viewBox="0 0 20 20" fill="currentColor">
                <path d="M11 3a1 1 0 100 2h2.586l-6.293 6.293a1 1 0 101.414 1.414L15 6.414V9a1 1 0 102 0V4a1 1 0 00-1-1h-5z"/>
                <path d="M5 5a2 2 0 00-2 2v8a2 2 0 002 2h8a2 2 0 002-2v-1a1 1 0 10-2 0v1H5V7h1a1 1 0 000-2H5z"/>
              </svg>
            </a>
          </div>
        </div>
      `
    }).join('')

    this.contentTarget.innerHTML = `<div class="space-y-3">${invoiceHtml}</div>`
  }

  showLoading() {
    this.loadingTarget.classList.remove('hidden')
    this.contentTarget.classList.add('hidden')
    if (this.hasErrorTarget) {
      this.errorTarget.classList.add('hidden')
    }
  }

  showContent() {
    this.loadingTarget.classList.add('hidden')
    this.contentTarget.classList.remove('hidden')

    if (this.hasContentWrapperTarget) {
      this.contentWrapperTarget.classList.remove('hidden')
    }

    if (this.hasErrorTarget) {
      this.errorTarget.classList.add('hidden')
    }
  }

  showError() {
    this.loadingTarget.classList.add('hidden')
    if (this.hasContentWrapperTarget) {
      this.contentWrapperTarget.classList.add('hidden')
    } else {
      this.contentTarget.classList.add('hidden')
    }
    if (this.hasErrorTarget) {
      this.errorTarget.classList.remove('hidden')
    }
  }

  // Determine which render method to use based on the presence of certain targets
  renderInvoices(invoices) {
    // If we're in a table context (has tbody as content target)
    if (this.contentTarget.tagName === 'TBODY') {
      this.renderInvoicesTable(invoices)
    } else {
      // Otherwise, we're in a card context
      this.renderInvoicesCard(invoices)
    }
  }

  renderInvoicesTable(invoices) {
    if (invoices.length === 0) {
      this.contentTarget.innerHTML = `
        <tr>
          <td colspan="${this.showDueValue ? 5 : 4}" class="px-6 py-4 text-center text-gray-500">No invoices yet.</td>
        </tr>
      `
      return
    }

    const invoiceHtml = invoices.map(invoice => {
      const date = new Date(invoice.period_start * 1000)
      const formattedDate = date.toLocaleDateString('en-US', {
        month: 'short',
        year: 'numeric'
      })

      const amount = (invoice.total / 100).toFixed(2)
      let statusClass = 'bg-yellow-100 text-yellow-800'
      if (invoice.status === 'paid') statusClass = 'bg-green-100 text-green-800'
      if (invoice.overdue) statusClass = 'bg-red-100 text-red-800'
      const statusLabel = invoice.overdue
        ? 'Overdue'
        : invoice.status.charAt(0).toUpperCase() + invoice.status.slice(1)

      return `
        <tr>
          <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
            <a href="${invoice.hosted_invoice_url}" target="_blank" class="text-indigo-600 hover:text-indigo-900 inline-flex items-center">
              ${invoice.number}
              <svg class="w-3 h-3 ml-1" viewBox="0 0 20 20" fill="currentColor">
                <path d="M11 3a1 1 0 100 2h2.586l-6.293 6.293a1 1 0 101.414 1.414L15 6.414V9a1 1 0 102 0V4a1 1 0 00-1-1h-5z"/>
                <path d="M5 5a2 2 0 00-2 2v8a2 2 0 002 2h8a2 2 0 002-2v-1a1 1 0 10-2 0v1H5V7h1a1 1 0 000-2H5z"/>
              </svg>
            </a>
          </td>
          <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
            ${formattedDate}
          </td>
          <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
            $${amount}
          </td>
          ${this.showDueValue ? `<td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">${invoice.due_date ? this.formatDueDate(invoice.due_date) : '—'}</td>` : ''}
          <td class="px-6 py-4 whitespace-nowrap">
            <span class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${statusClass}">
              ${statusLabel}
            </span>
          </td>
        </tr>
      `
    }).join('')

    this.contentTarget.innerHTML = invoiceHtml
  }

  formatDueDate(timestamp) {
    return new Date(timestamp * 1000).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
  }
}